import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '@/stores/appStore'
import { dingtalkLogin, emergencyLogin, setToken } from '@/services/api'
import { isInDingTalk, getAuthCode } from '@/services/dingtalk'
import './Login.css'

type Stage = 'detecting' | 'dingtalk' | 'dingtalk-failed' | 'emergency'

export function Login() {
  const navigate = useNavigate()
  const { loginSuccess } = useAppStore()
  const [stage, setStage] = useState<Stage>('detecting')
  const [message, setMessage] = useState('正在检测钉钉环境…')
  const [error, setError] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!isInDingTalk()) {
        if (!cancelled) setStage('dingtalk-failed')
        return
      }
      setStage('dingtalk')
      setMessage('正在进行钉钉免登…')
      try {
        const code = await getAuthCode(import.meta.env.VITE_DINGTALK_CORP_ID)
        const result = await dingtalkLogin(code)
        if (cancelled) return
        if (result.success && result.data) {
          setToken(result.data.token)
          loginSuccess(result.data.user)
          navigate('/', { replace: true })
        } else {
          setError(result.error || '钉钉免登失败')
          setStage('dingtalk-failed')
        }
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : '钉钉免登失败')
        setStage('dingtalk-failed')
      }
    })()
    return () => { cancelled = true }
  }, [loginSuccess, navigate])

  const handleEmergencySubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    const result = await emergencyLogin(username, password)
    if (result.success && result.data) {
      setToken(result.data.token)
      loginSuccess(result.data.user)
      navigate('/', { replace: true })
    } else {
      setError(result.error || '登录失败')
    }
    setSubmitting(false)
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <svg className="login-logo" viewBox="0 0 100 100">
            <rect fill="#e85d04" rx="20" width="100" height="100" />
            <path
              d="M30 70 L45 50 L55 60 L70 35 L75 40"
              stroke="white"
              strokeWidth="6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="35" cy="35" r="8" fill="white" />
          </svg>
          <h1>SenYao</h1>
          <p>森耀资源中台</p>
        </div>

        {stage === 'detecting' || stage === 'dingtalk' ? (
          <div className="login-status">
            <div className="login-spinner" />
            <div>{message}</div>
          </div>
        ) : stage === 'dingtalk-failed' ? (
          <>
            <div className="login-status">
              <p>{isInDingTalk() ? '钉钉免登失败，请在钉钉内重新打开。' : '请在钉钉工作台打开本应用。'}</p>
              {error && <div className="error-message" style={{ marginTop: 12 }}>{error}</div>}
            </div>
            <div
              className="login-emergency-toggle"
              onClick={() => { setStage('emergency'); setError('') }}
            >
              使用紧急账号登录
            </div>
          </>
        ) : (
          <form onSubmit={handleEmergencySubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username">用户名</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="请输入紧急账号"
                autoComplete="username"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">密码</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请输入密码"
                autoComplete="current-password"
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" className="login-btn" disabled={submitting}>
              {submitting ? '登录中…' : '登录'}
            </button>
            <div
              className="login-emergency-toggle"
              onClick={() => { setStage('dingtalk-failed'); setError('') }}
            >
              返回
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
